import { useLocalStorageState } from 'ahooks'
import cn from 'classnames'
import lodash from 'lodash'
import React, { useEffect, useState, useMemo, useCallback } from 'react'

import type { MCWithParams } from 'components/uiKit/Modal'
import Modal, { ModalFooter, ModalHeader } from 'components/uiKit/Modal'
import { ENUM_VALUE_MAP } from 'components/uiKit/SortPopover/constants'
import type { SortUiEnum } from 'components/uiKit/SortPopover/types'
import type {
  TemplateCollectionCreatedFor,
  TemplatesListSchemaFragment,
  EmployeePreferencesSchemaFragment,
  CollectionOrder,
} from 'gql/__generated__/graphql'
import { EmployeePreferencesTab, EditorTemplateAllTab } from 'gql/__generated__/graphql'
import { useGetEmployeePreferencesByProjectId } from 'gql/projects/apollo'
import { useTemplatesGetTemplatesAndCollections } from 'gql/templates/apollo'
import type { BlockMode } from 'services/Store/Project/enums'
import { SectionTypeEnum } from 'services/Store/Project/enums'
import { t } from 'services/Translation'

import CollectionsList from './CollectionsList'
import HeaderTemplate from './HeaderTemplate'
import { STEPS } from './Stepper/Stepper'
import Stepper from './Stepper/Stepper'
import Tags from './Tags'
import TemplatePickerFooter from './TemplatePickerFooter'
import * as s from './TemplatePickerModal.module.scss'
import TemplatesList from './TemplatesList'
import {
  employeePreferencesToOrder,
  getDefaultTemplateValue,
  SINGLE_MODE,
  singleBlockModes,
} from './helper'
import type { ITemplatePickerContext } from './hooks'
import {
  useSelected,
  TemplatePickerContext,
  useTags,
  STEP_BLOCK_MODE_MAP,
  getModeBySectionType,
  useEmployeePreferencesTemplate,
  getTemplateTagOptions,
  DEF_OPTION_TAG,
} from './hooks'
import { TemplatePickerMode, TemplatePickerModalType } from './types'

export type ICollectionType = {
  id: string
  createdFor?: TemplateCollectionCreatedFor
  name?: string
  templatesCount?: number
} | null

interface IParams {
  mode?: TemplatePickerMode
  sectionType?: SectionTypeEnum
  companyId: string
  projectId?: string
  sectionId?: string
  blocksByMode?: Record<string, boolean>
  modalType: TemplatePickerModalType
  employeePreferences?: Partial<EmployeePreferencesSchemaFragment> | null
  tagFromLocalStorage?: boolean
  showBrand?: boolean
  blockMode?: BlockMode[]
}

const USER_PREFERENCES_TAG_KEY_PICKER_MODAL = 'collections-tags-picker-modal'

// TODO: think about split containers - TemplatePickerModal company and editor
const TemplatePickerModal: MCWithParams<
  IParams,
  { selected: string[]; tab: string; template?: TemplatesListSchemaFragment }
> = ({
  onClose,
  params: {
    mode: defaultMode = TemplatePickerMode.multi,
    sectionType,
    companyId,
    projectId = '',
    sectionId,
    blocksByMode,
    modalType,
    employeePreferences,
    showBrand,
    blockMode,
  },
}) => {
  const { data: employeePreferencesData } = useGetEmployeePreferencesByProjectId(projectId)
  const preferencesTemplates = employeePreferencesData?.data?.employeePreferences?.templates
  const { defTab, defTag, defCollection } = useMemo(
    () => getDefaultTemplateValue(employeePreferencesData, projectId),
    [employeePreferencesData, projectId],
  )
  const [tab, setTab] = useState<EmployeePreferencesTab>(defTab)
  const [order, setOrder] = useState<SortUiEnum>(
    employeePreferencesToOrder(employeePreferences?.templates?.order || null),
  )
  const [search, setSearch] = useState<string | undefined>('')
  const [collection, setCollection] = useState<ICollectionType>(defCollection)
  const [templates, setTemplates] = useState<TemplatesListSchemaFragment[]>([])
  const [columns, setColumns] = useLocalStorageState('templateColumn', { defaultValue: 3 })
  const [selectedSteps, setSelectedSteps] = useState<number[]>([])
  const defaultBlockMode = blockMode?.length ? blockMode : getModeBySectionType(sectionType)
  const isTest = sectionType === SectionTypeEnum.test
  const isCover = sectionType === SectionTypeEnum.cover
  const showTags = Boolean(collection?.id)
  const isAddModal = modalType === TemplatePickerModalType.add
  const isTestAndModalAdd = isTest && isAddModal
  const currentStep = isTestAndModalAdd
    ? STEPS.findIndex((step) => defaultBlockMode.includes(step.value)) || 0
    : null
  const [step, setStep] = useState<number | null>(currentStep)
  const updatedMod = isTestAndModalAdd ? STEP_BLOCK_MODE_MAP[step || 0] : defaultBlockMode

  const { updatePreferences } = useEmployeePreferencesTemplate({
    collection,
    order,
    preferencesTemplates,
    tab,
    projectId,
  })

  const { tag, setTag, tags } = useTags({
    employeePreferencesTag: defTag,
    collectionId: collection?.id,
    companyId,
    projectId,
    updatePreferences,
    userPreferencesKey: USER_PREFERENCES_TAG_KEY_PICKER_MODAL,
    mode: updatedMod,
  })

  const tagOptions = useMemo(() => [DEF_OPTION_TAG, ...getTemplateTagOptions(tags)], [tags])
  const { selected, setSelected, addItem, clearSelect, removeByIndex } = useSelected()
  const allSelected = useMemo(
    () => lodash.compact([selected.cover, selected.start, selected.end, ...selected.multi]),
    [selected],
  )
  const hasSingleMode =
    (isCover || (step !== null && singleBlockModes.includes(STEPS[step]?.value))) &&
    defaultMode !== TemplatePickerMode.change
  const mode = hasSingleMode ? TemplatePickerMode.single : defaultMode

  const onClick = useCallback(
    (value: TemplatesListSchemaFragment, e: React.MouseEvent) => {
      setSelectedSteps((prev) => lodash.uniq([...prev, step as number]))
      if (mode === TemplatePickerMode.single) {
        setSelected((prev) => ({ ...prev, [value.blocks[0].mode]: value.id }))
      }
      if (mode === TemplatePickerMode.change) {
        const singleModeTemplate = SINGLE_MODE.includes(value.blocks[0].mode)
        if (singleModeTemplate) {
          setSelected((prev) => ({ ...prev, [value.blocks[0].mode]: value.id }))
        } else {
          setSelected((prev) => ({ ...prev, multi: [value.id] }))
        }
      }
      if (mode === TemplatePickerMode.multi) {
        addItem(value, e)
      }
    },
    [addItem, mode, step, setSelected],
  )

  const onSubmit = useCallback(
    async (params: { selected: string[]; template?: TemplatesListSchemaFragment }) =>
      onClose({ ...params, tab }),
    [onClose, tab],
  )

  const handleTagChange = useCallback(
    (value: EmployeePreferencesTab) => {
      setTab(value)
      updatePreferences({ paramsTab: value })
    },
    [setTab, updatePreferences],
  )

  const handleCollectionChange = useCallback(
    (value: ICollectionType) => {
      setSearch('')
      setCollection(value)
      updatePreferences({ paramsCollection: value })
    },
    [setCollection, updatePreferences],
  )

  const templateProps = useMemo(
    () => ({
      selected: selected.multi,
      onClick,
      removeByIndex,
      search,
      companyId,
      projectId,
      tab,
      allSelected,
    }),
    [selected, search, projectId, allSelected, companyId, tab, removeByIndex, onClick],
  )

  const contextValue: ITemplatePickerContext = useMemo(
    () => ({
      companyId,
      collection,
      projectId,
      sectionId,
      setCollection: handleCollectionChange,
      setStep,
      step,
      sectionType,
      mode,
      onClick,
      removeByIndex,
      search,
      setSearch,
      selected,
      setTemplates,
      allSelected,
      blocksByMode,
      tag: tag.id,
      setTag,
      showTags,
      columns,
      setColumns,
      tab,
      order,
      setOrder,
      showBrand,
      tagOptions,
      blockMode: updatedMod,
    }),
    [
      setTag,
      allSelected,
      tagOptions,
      blocksByMode,
      collection,
      columns,
      companyId,
      handleCollectionChange,
      mode,
      onClick,
      order,
      projectId,
      removeByIndex,
      search,
      sectionId,
      sectionType,
      selected,
      setColumns,
      showBrand,
      showTags,
      step,
      tab,
      tag,
      updatedMod,
    ],
  )

  const collectionsMode = getModeBySectionType(sectionType)

  const { data, loading } = useTemplatesGetTemplatesAndCollections({
    mode: collectionsMode,
    companyId,
    data: {
      projectId,
      mode: collectionsMode,
      query: search,
      tab: EditorTemplateAllTab.collections,
      ...(order && {
        order: {
          field: ENUM_VALUE_MAP[order][0][0],
          value: ENUM_VALUE_MAP[order][0][1],
        } as CollectionOrder,
      }),
    },
  })

  const Content = {
    [EmployeePreferencesTab.collections]: (
      <CollectionsList data={data} loading={loading} templateProps={templateProps} />
    ),
    [EmployeePreferencesTab.favorites]: (
      <TemplatesList tab={EmployeePreferencesTab.favorites} templateProps={templateProps} />
    ),
    [EmployeePreferencesTab.recently_used]: (
      <TemplatesList tab={EmployeePreferencesTab.recently_used} templateProps={templateProps} />
    ),
  }

  useEffect(() => {
    if (tab !== EmployeePreferencesTab.collections) {
      handleCollectionChange(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collection?.id])

  useEffect(() => {
    if (mode === TemplatePickerMode.change) {
      handleTagChange(EmployeePreferencesTab.collections)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // TODO: remove after split containers - TemplatePickerModal
  useEffect(() => {
    setCollection(defCollection)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!defCollection])

  return (
    <TemplatePickerContext.Provider value={contextValue}>
      <Modal
        minHeight='100%'
        name='pickerTemplate'
        styleType='tabs'
        title={t('modal.pickerTemplate.title')}
      >
        <ModalHeader>
          <Stepper modalType={modalType} selectedSteps={selectedSteps} />
          <HeaderTemplate dataCollections={data} />
        </ModalHeader>
        <div className={cn(s.body, { [s.extraPadding]: isCover })}>
          <Tags setTab={handleTagChange} />
          <div className={s.content}>
            {collection?.id && tab === EmployeePreferencesTab.collections ? (
              <TemplatesList
                tab={EmployeePreferencesTab.collections}
                templateProps={templateProps}
              />
            ) : (
              Content[tab] || Content[EmployeePreferencesTab.collections]
            )}
          </div>
        </div>

        <ModalFooter>
          <TemplatePickerFooter
            clearSelect={clearSelect}
            hideAddAll={tab === EmployeePreferencesTab.collections && !collection?.id}
            onSubmit={onSubmit}
            setSelected={setSelected}
            tab={tab}
            templates={templates}
          />
        </ModalFooter>
      </Modal>
    </TemplatePickerContext.Provider>
  )
}

export default React.memo(TemplatePickerModal)
